/**
 * UserOrgsContext — shared org-selection state for the entire v2 app tree.
 *
 * ANA-190: the bug was that useUserOrgs held per-hook local state, so
 * switchOrg() from GlobalNavBar only updated that hook's instance — the
 * useProjects instance (separate useState initialiser) never re-read
 * localStorage and its query key stayed unchanged → no refetch on switch.
 *
 * Fix: a single UserOrgsProvider owns currentOrgId + switchOrg. Every
 * useUserOrgs() call reads the same context value, so one switchOrg() call
 * is instantly visible to all consumers (including useProjects), driving a
 * new query-key and an immediate project-list refetch.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { navigate } from 'wouter/use-browser-location';
import type { OrgInfo } from '@/components/v2/layout/UserProfileMenu';
import { clearStoredConversationLifecycles } from '@/hooks/v2/useConversationLifecycle';
import { useAuth } from '@/hooks/useAuth';

// Exported for the rare caller that must switch straight into a full page
// reload (e.g. CreateDemo's "Open demo" CTA) — see ANA-367 P1 review note on
// switchOrg below for why that path bypasses switchOrg's own guard.
export const USER_ORGS_STORAGE_KEY = 'anaka_current_org_id';
const STORAGE_KEY = USER_ORGS_STORAGE_KEY;

interface UserOrgsContextValue {
  orgs: OrgInfo[];
  currentOrgId: string | null;
  switchOrg: (orgId: string) => void;
  isLoading: boolean;
}

const UserOrgsContext = createContext<UserOrgsContextValue | null>(null);

async function fetchOrgs(): Promise<OrgInfo[]> {
  const res = await fetch('/api/v1/profile/organisations', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch organisations');
  const data = (await res.json()) as { data?: OrgInfo[] };
  return (data.data ?? []) as OrgInfo[];
}

export function UserOrgsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY),
  );

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['user-orgs'],
    queryFn: fetchOrgs,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  });

  // Self-heal: if the stored org isn't in the fetched list, reset to the first
  // org. This covers: stale localStorage from another env (403 on scoped fetch),
  // and the nondeterministic-default bug where orgs[0] is an empty workspace.
  useEffect(() => {
    if (orgs.length === 0) return;
    if (!currentOrgId || !orgs.some((o) => o.id === currentOrgId)) {
      const firstId = orgs[0].id;
      localStorage.setItem(STORAGE_KEY, firstId);
      setCurrentOrgId(firstId);
    }
  }, [orgs, currentOrgId]);

  const switchOrg = useCallback(
    (orgId: string) => {
      if (orgId === currentOrgId) return; // no-op: already the active org
      // ANA-367: reject an orgId the user isn't a member of (stale deep link,
      // race with the org-list fetch, bad programmatic caller) rather than
      // writing it and relying on the self-heal effect to catch it a render
      // later. Only enforced once the org list has loaded — an empty `orgs`
      // means "not fetched yet", not "no valid orgs", so we can't validate
      // against it.
      //
      // This guard is for the in-SPA switch path only (e.g. GlobalNavBar's
      // org picker, which only ever offers orgs already in this list). A
      // caller switching into a freshly-created org it knows is valid but
      // that hasn't reached the shared ['user-orgs'] query yet (e.g. a
      // just-seeded demo org, followed by a full reload that will refetch
      // and self-heal) should write USER_ORGS_STORAGE_KEY directly instead
      // of calling switchOrg — see CreateDemo.tsx.
      if (orgs.length > 0 && !orgs.some((o) => o.id === orgId)) {
        console.warn(`switchOrg: dropped switch to org ${orgId} — not in the fetched org list`);
        return;
      }
      localStorage.setItem(STORAGE_KEY, orgId);
      // Data isolation on org switch: a conversation id is owner-scoped to one
      // org, so drop any persisted conversation session before activating the
      // new org (the conversation provider also tears down its in-memory
      // lifecycle when currentOrgId changes). Then always land on the new org's
      // home — never leave the user on the previous org's project/call page.
      clearStoredConversationLifecycles();
      setCurrentOrgId(orgId);
      navigate('/');
    },
    [currentOrgId, orgs],
  );

  return (
    <UserOrgsContext.Provider value={{ orgs, currentOrgId, switchOrg, isLoading }}>
      {children}
    </UserOrgsContext.Provider>
  );
}

export function useUserOrgs(): UserOrgsContextValue {
  const ctx = useContext(UserOrgsContext);
  if (!ctx) throw new Error('useUserOrgs must be used inside UserOrgsProvider');
  return ctx;
}
